import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { loginRequest, msalInstance } from './msalConfig'

/** Thrown for any non-2xx `/api/*` response, carrying the status for callers that branch on it (e.g. 403 -> access-denied copy). */
export class ApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

/**
 * Resolves the bearer token for the current operator.
 *
 * `acquireTokenSilent` (a cached-token or silent-refresh call, no UI) is
 * always tried first. Only when Entra actually requires interaction — an
 * expired session, a first-time consent — do we fall back to a popup so the
 * in-flight `/api/*` call can still complete without losing the caller's
 * place in the app; a full-page `loginRedirect` is the last resort when the
 * popup itself is blocked, since that trades this call for a fresh page
 * load.
 */
async function acquireAccessToken(): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]
  if (!account) {
    throw new Error('No signed-in CHU account. Sign in before calling /api/*.')
  }

  const request = { ...loginRequest, account }

  try {
    const result = await msalInstance.acquireTokenSilent(request)
    return result.accessToken
  } catch (error) {
    if (!(error instanceof InteractionRequiredAuthError)) {
      throw error
    }
  }

  try {
    const result = await msalInstance.acquireTokenPopup(request)
    return result.accessToken
  } catch {
    // Popup blocked or dismissed: fall back to a redirect. This navigates
    // away, so the promise below only ever settles if that call itself
    // throws (e.g. redirect blocked too); the happy path resumes the
    // original call after Entra sends the browser back.
    await msalInstance.acquireTokenRedirect(request)
    throw new Error('Redirecting to sign-in to renew the CHU-GPT Admin session.')
  }
}

function fetchWithToken(path: string, opts: RequestInit, token: string): Promise<Response> {
  const headers = new Headers(opts.headers)
  headers.set('Authorization', `Bearer ${token}`)
  // A JSON string body with no Content-Type is not parsed by Fastify, so the
  // handler sees an EMPTY body and rejects the request for a missing field
  // that was in fact sent. The People page hit exactly that: minting a key
  // returned "email is required and must be an email address" while passing
  // a perfectly good email.
  //
  // Every caller before that set the header by hand, which worked and made
  // the omission invisible until someone forgot. Defaulting it here removes
  // the trap for good; `has()` means a caller sending FormData or another
  // content type still wins.
  if (typeof opts.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  return fetch(path, { ...opts, headers })
}

/**
 * Reads a Conditional Access claims challenge out of a 401 body, if present.
 * The backend emits `{ error: "interaction_required", claims }` when a Graph
 * on-behalf-of exchange needs the operator to step up (MFA). Anything that is
 * not exactly that shape returns undefined, so an ordinary 401/403 still
 * surfaces as an `ApiError`.
 */
function parseInteractionChallenge(body: string): string | undefined {
  try {
    const parsed = JSON.parse(body) as { error?: string; claims?: string }
    if (parsed.error === 'interaction_required' && typeof parsed.claims === 'string' && parsed.claims.length > 0) {
      return parsed.claims
    }
  } catch {
    // Not JSON: an ordinary error body, not a challenge.
  }
  return undefined
}

/**
 * Forces an interactive token acquisition carrying the CA claims challenge,
 * which prompts the operator for the required step-up (MFA) even when their
 * normal sign-in never does. Returns the fresh access token — which now
 * satisfies the challenge — for the immediate retry. Falls back to a
 * full-page redirect only if the popup is blocked (that navigates away, so
 * the caller's promise never resolves on that branch).
 */
async function stepUp(claims: string): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]
  if (!account) {
    throw new Error('No signed-in CHU account for step-up authentication.')
  }
  const request = { ...loginRequest, account, claims }
  try {
    const result = await msalInstance.acquireTokenPopup(request)
    return result.accessToken
  } catch {
    await msalInstance.acquireTokenRedirect(request)
    throw new Error('Redirecting to complete multi-factor authentication for this action.')
  }
}

/**
 * Fetch wrapper for every `/api/*` call: acquires the current operator's
 * access token (see `acquireAccessToken`) and attaches it as
 * `Authorization: Bearer <token>`, preserving any caller-supplied method,
 * body, or headers. Throws `ApiError` on a non-2xx response; the backend is
 * the real authority on access (401/403), this wrapper never second-guesses
 * it client-side beyond attaching the token.
 *
 * The one exception is a 401 carrying a Conditional Access claims challenge
 * (`interaction_required`): the operator is stepped up to MFA once and the
 * call is retried a single time with the fresh token. A retry that still
 * fails surfaces as an `ApiError` like any other.
 */
export async function api(path: string, opts: RequestInit = {}): Promise<Response> {
  const accessToken = await acquireAccessToken()
  let response = await fetchWithToken(path, opts, accessToken)
  if (response.ok) {
    return response
  }

  let body = await response.text().catch(() => '')
  if (response.status === 401) {
    const claims = parseInteractionChallenge(body)
    if (claims) {
      // The retry replays the original request verbatim, so it assumes an
      // idempotent handler with a replayable body (every caller here sends a
      // JSON string or no body). The step-up 401 comes from the OBO token
      // exchange, which runs before any handler side effect, so the replay is
      // safe for the current routes; a future streaming upload would not be.
      const steppedToken = await stepUp(claims)
      response = await fetchWithToken(path, opts, steppedToken)
      if (response.ok) {
        return response
      }
      body = await response.text().catch(() => '')
    }
  }

  throw new ApiError(response.status, body || response.statusText)
}

/** Convenience wrapper for the common case: an `/api/*` call whose body is JSON. */
export async function apiJson<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const response = await api(path, opts)
  return (await response.json()) as T
}
